<template>
  <F360ScreenFrame
    class="insights-agente-body-frame insights-agente-body-frame--list"
  >
    <div class="insights-ia-list">
      <InsightsIALoadingState v-if="loading" />

      <InsightsIAEmptyState
        v-else-if="!insights.length && !loading"
        description="Nenhum insight encontrado para os filtros selecionados."
      />

      <div
        v-else-if="insights.length && !loading"
        class="insights-ia-list__summary"
      >
        <div v-if="primarySummaryItem" class="insights-ia-list__summary-main">
          <F360Iconia />
          <strong>
            {{ primarySummaryItem.value }} {{ primarySummaryItem.label }}
          </strong>

          <F360TooltipBase
            tooltip="<b>O agente de DRE gera insights via IA.</b> Sempre valide os resultados antes de tomar decisões"
            type="primary"
            placement="bottom"
          >
            <MaterialIcon
              icon="info"
              size="12px"
              class="insights-ia-list__summary-info"
            />
          </F360TooltipBase>
        </div>

        <span class="insights-ia-list__summary-divider" />

        <div class="insights-ia-list__summary-items">
          <div
            v-for="item in secondarySummaryItems"
            :key="item.label"
            class="insights-ia-list__summary-item"
            :class="`insights-ia-list__summary-item--${item.severity}`"
          >
            <strong v-if="item.value > 0">{{ item.value }}</strong>
            <span v-if="item.value > 0">{{ item.label }}</span>
          </div>
        </div>

        <InsightsIAActions
          :show-view-report="false"
          :export-disabled="!insights.length"
          @ExportData="handleExportData"
        />
      </div>

      <div v-if="insights.length && !loading">
        <el-row :gutter="12">
          <el-col
            v-for="(insight, index) in insights"
            :key="insight.id"
            :span="handleSpan(index)"
            class="insights-ia-list__items"
          >
            <article class="insights-ia-list__insight">
              <div class="insights-ia-list__insight-header">
                <div class="insights-ia-list__title">
                  <InsightsOrderNumber :order="insight.order" />
                  <h3>{{ insight.title }}</h3>
                </div>

                <InsightsIABadge
                  :label="defineSeverityLabel(insight.severity)"
                  :variant="defineSeverityVariant(insight.severity)"
                />
              </div>

              <div class="insights-ia-list__insight__resumo">
                <strong>
                  <span class="titulo">Resumo</span>
                  <img
                    :src="iaIcon"
                    alt="inteligência artificial"
                    class="insights-ia-list__insight__resumo-icon"
                  />
                </strong>
                <div v-if="insight.isExpanded">
                  <p v-html="sanitizeInsightSummary(insight.summary)"></p>
                </div>
                <p v-else>{{ truncateSummary(insight.summary) }}</p>
              </div>

              <InsightsIAInsightContent
                v-if="insight.isExpanded"
                :content="{
                  summary: insight.summary,
                  riskDescription: insight.riskDescription,
                  suggestedActions: insight.suggestedActions
                }"
              />

              <footer class="insights-ia-list__feedback">
                <button
                  type="button"
                  class="insights-ia-list__toggle"
                  @click="toggleInsight(insight.id)"
                >
                  {{ insight.isExpanded ? "Ver menos" : "Ver mais" }}
                  <MaterialIcon
                    :icon="
                      insight.isExpanded
                        ? 'keyboard_arrow_up'
                        : 'keyboard_arrow_down'
                    "
                    size="14px"
                  />
                </button>
                <F360InsightFeedback
                  label="Este insight foi útil para você?"
                  :useful-insight="insight.usefulInsight"
                  @feedback="handleCsat($event, insight)"
                />
              </footer>
            </article>
          </el-col>
        </el-row>
      </div>
    </div>
  </F360ScreenFrame>
</template>

<script setup lang="ts">
import MaterialIcon from "@/ui/components/Icons/MaterialIcon/MaterialIcon.vue";
import InsightsIAActions from "../../components/InsightsIAActions.vue";
import InsightsIALoadingState from "../../components/InsightsIALoadingState.vue";
import InsightsIAEmptyState from "../../components/InsightsIAEmptyState.vue";
import InsightsOrderNumber from "../../components/InsightsOrderNumber.vue";
import InsightsIAInsightContent from "../../components/InsightsIAInsightContent.vue";
import F360Iconia from "@/ui/components/Icons/ia/F360Iconia.vue";
import F360ScreenFrame from "@/ui/components/Layout/Main/ScreenFrame/F360ScreenFrame.vue";
import InsightsIABadge from "@/ui/components/Bagdes/InsightsBagde/InsightsIABadge.vue";
import { useAgenteDeDreInsigthsStore } from "../../store/useAgenteDeDreInsigthsStore";
import { storeToRefs } from "pinia";
import { computed } from "vue";
import { InsightsIABadgeVariant } from "@/ui/components/Bagdes/InsightsBagde/InsightsBagdeTypes.js";
import { TAnaliseInsight } from "../../services/InsightsAIDreGateway";
import F360TooltipBase from "@/ui/components/Tooltips/TooltipBase/F360TooltipBase.vue";
import iaIcon from "@/ui/assets/financas/insigths/icone-ia-blue.svg";
import { EInsightsIASeverity } from "../../../../@types/InsightsEnuns.js";
import { sanitizeHtml } from "@/core/common/sanitizeHtml";
import F360InsightFeedback from "../../../../components/F360InsightFeedback.vue";

const emit = defineEmits<{
  (e: "csatFeedback", value: { feedback: "true" | "false"; id: string }): void;
  (e: "exportData"): void;
}>();

const agenteDeDreInsigthsStore = useAgenteDeDreInsigthsStore();
const { relatorio, loading } = storeToRefs(agenteDeDreInsigthsStore);

const primarySummaryItem = computed(() => relatorio.value.summaryItems);
const secondarySummaryItems = computed(
  () => relatorio.value.secondarySummaryItems
);
const insights = computed(() => relatorio.value.insights);

const toggleInsight = (id: string) => {
  agenteDeDreInsigthsStore.setInsightExpanded(
    relatorio.value,
    id,
    !insights.value.find((insight) => insight.id.toString() === id)
      ?.isExpanded || false
  );
};

const defineSeverityLabel = (severity: EInsightsIASeverity) => {
  switch (severity) {
    case EInsightsIASeverity.Critical:
      return "Crítico";
    case EInsightsIASeverity.Warning:
      return "Atenção";
    case EInsightsIASeverity.Info:
      return "Info";
    default:
      return "Info";
  }
};

const defineSeverityVariant = (
  severity: EInsightsIASeverity
): InsightsIABadgeVariant => {
  switch (severity) {
    case EInsightsIASeverity.Critical:
      return InsightsIABadgeVariant.Critical;
    case EInsightsIASeverity.Warning:
      return InsightsIABadgeVariant.Warning;
    case EInsightsIASeverity.Info:
      return InsightsIABadgeVariant.Infos;
    default:
      return InsightsIABadgeVariant.Primary;
  }
};

const handleCsat = (value: "true" | "false", insight: TAnaliseInsight) => {
  if (insight.usefulInsight != null) return;

  agenteDeDreInsigthsStore.setInsightUsefulInsight(
    relatorio.value,
    insight.id,
    value === "true" ? true : false
  );
  agenteDeDreInsigthsStore.setInsights(relatorio.value, insights.value);
  emit("csatFeedback", { feedback: value, id: insight.id });
};

const handleExportData = () => {
  emit("exportData");
};

const sanitizeInsightSummary = (summary: string) => sanitizeHtml(summary);

const truncateSummary = (text: string, maxLength = 155) => {
  const plainText = sanitizeHtml(text)
    .replace(/<[^>]*>/g, "")
    .trim();

  if (plainText.length <= maxLength) {
    return plainText;
  }

  return `${plainText.slice(0, maxLength).trimEnd()}...`;
};

const handleSpan = (index: number) => {
  const insightsLength = insights.value.length;

  if (insightsLength <= 2) {
    return 24;
  }

  if (index === 0) {
    return 24;
  }

  if (insightsLength === 4 && index === insightsLength - 1) {
    return 24;
  }

  return 12;
};
</script>

<style scoped lang="scss">
.insights-ia-list {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__summary {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 16px;
    border: 1px solid _map.get($map: $neutro, $key: 200);
    border-radius: 4px;
    background: _map.get($map: $neutro, $key: 0);
  }

  &__summary-main {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 180px;

    strong {
      color: _map.get($map: $neutro, $key: 900);
      font-size: 14px;
      line-height: 18px;
    }
  }

  &__summary-info {
    color: _map.get($map: $neutro, $key: 500);
  }

  &__summary-divider {
    width: 1px;
    height: 32px;
    background: _map.get($map: $neutro, $key: 200);
  }

  &__summary-items {
    display: flex;
    align-items: center;
    gap: 28px;
    flex: 1;
  }

  &__summary-item {
    display: flex;
    align-items: center;
    flex-direction: column;
    gap: 2px;
    min-width: 44px;

    strong {
      color: _map.get($map: $azulPrincipal, $key: 500);
      font-size: 14px;
      line-height: 16px;
    }

    span {
      color: _map.get($map: $neutro, $key: 900);
      font-size: 11px;
      line-height: 14px;
    }

    &--Info strong {
      color: _map.get($map: $azulPrincipal, $key: 700);
    }

    &--Success span {
      color: _map.get($map: $verde, $key: 700);
    }

    &--Critical strong {
      color: _map.get($map: $vermelho, $key: 700);
    }

    &--Warning strong {
      color: _map.get($map: $amarelo, $key: 700);
    }
  }

  &__insight {
    display: flex;
    flex-direction: column;
    border: 1px solid _map.get($map: $neutro, $key: 200);
    border-radius: 4px;
    background: _map.get($map: $neutro, $key: 0);
    overflow: hidden;

    &__resumo {
      display: flex;
      flex-direction: column;
      align-items: left;
      padding: 16px;

      .titulo {
        font-size: 12px;
        line-height: 14px;
        text-transform: uppercase;
        color: _map.get($map: $neutro, $key: 500);
      }

      p {
        font-family: "Plus Jakarta Sans", sans-serif;
        font-weight: 400;
        font-size: 14px;
        line-height: 22.4px;
        color: _map.get($map: $neutro, $key: 900);
        margin: 10px 0 0;
      }
    }

    &__resumo-icon {
      width: 11px;
      height: 11px;
      color: _map.get($map: $azulPrincipal, $key: 500);
      margin-left: 5px;
    }
  }

  &__insight-header {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 12px;
    padding: 14px 16px;
    border-bottom: 1px solid _map.get($map: $neutro, $key: 200);
  }

  &__items {
    display: flex;
    flex-direction: column;
    margin-bottom: 12px;
  }

  &__title {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  h3 {
    margin: 0;
    color: _map.get($map: $neutro, $key: 900);
    font-size: 13px;
    line-height: 18px;
  }

  &__feedback {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 16px;
    border-top: 1px solid _map.get($map: $neutro, $key: 200);
  }

  &__toggle {
    display: inline-flex;
    align-items: center;
    border: 0;
    background: transparent;
    font-size: 11px;
    line-height: 14px;
    cursor: pointer;
    gap: 2px;
    color: _map.get($map: $azulPrincipal, $key: 500);
  }

  @media screen and (max-width: 768px) {
    &__summary,
    &__insight-header,
    &__feedback {
      align-items: flex-start;
      flex-direction: column;
    }

    &__summary-divider {
      display: none;
    }

    &__summary-items {
      width: 100%;
      justify-content: space-between;
    }
  }
}
</style>
